import { useAppStore } from "@hooks/useAppStore";
/* ======== Types ========= */
import type { Plan, ProductType } from "@memorang/types";
import { Box, Button } from "@memorang/ui";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { t } from "i18next";
import { getAccessDurationTemplate } from "../../helpers/purchase";
/* ======== Components ========= */
import FeaturesSection from "./FeaturesSection";
import PlansSection, { type PlansSectionProps } from "./PlansSection";
import ProductIconContainer, {
	type ProductIconProps,
} from "./ProductIconContainer";
import RecommendedPlanText from "./RecommendedPlanText";

type Props = {
	features: string[];
	handleRestorePurchase: () => void;
	recommendedPlan?: Plan;
	handleTracking: (eventName: string, params?: Record<string, string>) => void;
	disabled?: boolean;
	loading?: boolean;
	handlePurchase: () => void;
	productType?: ProductType;
	hasAccess?: boolean;
	purchasable?: boolean;
	fromHomePage?: boolean;
	handleClickAccessHere: () => void;
} & PlansSectionProps &
	ProductIconProps;

const IAPModalView = ({
	disabled,
	handlePurchase,
	loading,
	sortedPlans,
	selectedPlan,
	setSelectedPlan,
	expiryDate,
	features,
	url,
	numItems,
	productType = "STUDY_PACK",
	selectedDate,
	recommendedPlan,
	hasAccess,
	purchasable,
	fromHomePage,
	handleClickAccessHere,
	handleTracking,
}: Props) => {
	const tosLink = useAppStore((store) => store.app.links.tos);

	const purchaseDisclaimerText = t("purchase.paywall.purchaseDisclaimer", {
		tosLink,
	});

	const getCta = () => {
		if (hasAccess) {
			return t("account.extendValidity");
		}
		switch (productType) {
			case "STUDY_PACK":
				return t("common.unlock");
			default:
				return `Unlock for $${selectedPlan?.amount}`;
		}
	};
	const cta = getCta();

	const UpgradeButton = () => (
		<Button
			mode="contained"
			disabled={disabled}
			uppercase={false}
			onPress={handlePurchase}
			loading={loading}
		>
			{cta}
		</Button>
	);

	const AccessHereButton = () => (
		<Button
			disabled={disabled || loading}
			uppercase={false}
			onPress={handleClickAccessHere}
		>
			{t("purchase.paywall.accessHere")}
		</Button>
	);

	const Disclaimer = () => (
		<Box alignItems="center">
			<CustomMarkdownRenderer
				text={purchaseDisclaimerText}
				variant="explanation"
				fontSize={10}
				useCustomLink={false}
			/>
		</Box>
	);

	const yearPlan = sortedPlans.find((plan) => plan.duration === 12);

	const accessDurationText = getAccessDurationTemplate(
		selectedPlan,
		expiryDate,
		yearPlan,
	);

	const showAccessDurationText = productType === "STUDY_PACK" && purchasable;
	const showPlansSection = productType === "STUDY_PACK" && purchasable;
	const showUpgradeButton = purchasable;
	const showFeatures = features.length > 0;
	const showProductIcon = url;
	const showDisclaimer = purchaseDisclaimerText.length > 0 && purchasable;

	return (
		<Box paddingHorizontal={24} paddingTop={24} flex={1} gap={24}>
			{showProductIcon && (
				<ProductIconContainer
					url={url}
					numItems={numItems}
					fromHomePage={fromHomePage}
					selectedDate={selectedDate}
				/>
			)}
			{showFeatures && <FeaturesSection features={features} />}
			{showPlansSection && (
				<PlansSection
					sortedPlans={sortedPlans}
					selectedPlan={selectedPlan}
					handleTracking={handleTracking}
					setSelectedPlan={setSelectedPlan}
					expiryDate={expiryDate}
				/>
			)}
			{showAccessDurationText && (
				<CustomMarkdownRenderer
					fontSize={14}
					textAlign="center"
					text={`${accessDurationText}.`}
				/>
			)}
			<Box gap={16}>
				{showUpgradeButton && <UpgradeButton />}
				{hasAccess && <AccessHereButton />}
			</Box>
			{recommendedPlan && purchasable && showAccessDurationText && (
				<RecommendedPlanText
					recommendedPlan={recommendedPlan}
					selectedDate={selectedDate}
				/>
			)}
			{showDisclaimer && <Disclaimer />}
		</Box>
	);
};

export default IAPModalView;
